/**
 * @module experience/shop/shared-browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// Core Libraries
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';

// Services
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { LoggerService } from '@core/logger/services/logger.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';

// Interface
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { BrowseEntryConfigData } from '@core/aem/interfaces/common/browse-entry.interface';
import { ZipCodeEntryChangeInterface } from '@experience/shop/shared-browse/interfaces/zip-code-entry-change.interface';
import { Address } from '@support/api/proxies/responses/api-2004.interface';

// Model
import { ValidationMessageModel } from '@experience/shop/shared-browse/models/validation-message.model';

// Constants
import {
  SHARED_BROWSE_CONSTANTS,
  SHARED_BROWSE_COMPONENTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { APIError } from '@support/api/common/error.interface';
import { ZipCodeRequiredGuardService } from '@experience/shop/services/zip-code-required-guard.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tmo-zip-code-validation',
  templateUrl: './zip-code-validation.component.html',
})
export class ZipCodeValidationComponent implements OnInit, OnDestroy {
  /**
   * Configurable data from parent component
   */
  @Input() public zipCodeConfigurableData: BrowseEntryConfigData;
  /**
   * zip code validation form
   */
  @Input() public zipCodeValidationForm: FormGroup;

  /**
   * Output Event Emitter for accountForm Change
   */
  @Output() public formChange: EventEmitter<ZipCodeEntryChangeInterface>;
  /**
   * validation message for Zipcode
   */
  public validationMessage: ValidationMessageModel;
  /**
   * store flag for api failure.
   */
  public apiError: boolean;
  /**
   * Zip Code validation pattern
   */
  public zipCodeRegexPattern: string;
  /**
   * Constants to store mapping of field to fieldValidationMapping
   */
  // TODO: Create proper mapping for fieldValidation Constants
  public fieldValidationConstants: ObjectInterface;

  /**
   * Constants to store billingType
   */
  public billingType: string;

  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * Holds the address returned from 2004 API
   */
  public suggestedAddresses: Address[];

  /**
   * Boolean holds spinner
   */
  public showSpinner: boolean;

  /**
   * Zipcode entered in the form
   */
  public enteredZip: string;
  constructor(
    private sharedBrowseService: SharedBrowseService,
    private logger: LoggerService,
    private zipCodeRequiredGuardService: ZipCodeRequiredGuardService,
    private stateStoreUtilsService: StateStoreUtilsService
  ) {
    this.formChange = new EventEmitter();
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Load the initial configuration of zip-code-validation components.
   * @returns void
   */
  public ngOnInit(): void {
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.ZIP_CODE,
      ' Entering ngOnInit() in ' + SHARED_BROWSE_COMPONENTS.ZIP_CODE
    );
    this.fieldValidationConstants = this.sharedBrowseService.getFieldValidationConstants();
    if (this.fieldValidationConstants) {
      this.zipCodeRegexPattern = this.sharedBrowseService.getRegexPatternForZipCode(
        this.fieldValidationConstants.ZIP_CODE
      );
    }
    this.billingType = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus();
    // Initial component data
    this.validationMessage = null;
    // @TODO Krishna not using this after testing all the scenarios remove
    // this.buildZipCodeValidationForm();
    this.sharedBrowseService.zipCodeNotAvailableSource
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((value: boolean) => {
        if (value) {
          this.zipCodeValidationForm.controls.zip.reset();
          this.suggestedAddresses = null;
          this.enteredZip = null;
          this.zipCodeValidationForm.controls.zip.reset();
        }
      });
    if (this.zipCodeValidationForm.controls.zip.value) {
      this.getCityNameForZipCode(this.zipCodeValidationForm.controls.zip.value);
    }
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.ZIP_CODE,
      ' Exiting ngOnInit() in ' + SHARED_BROWSE_COMPONENTS.ZIP_CODE
    );
  }

  /**
   * Method to destroy service data.
   * @returns void
   */
  public ngOnDestroy(): void {
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.ZIP_CODE,
      ' Entering and exiting ngOnDestroy() in ' + SHARED_BROWSE_COMPONENTS.ZIP_CODE
    );
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Event Handler passing data to parent on input blur
   * @returns void
   */
  public updateZipCodeInformation(newZip: string = ''): void {
    const maxLengthUserCanEnter = 5;
    let tempZip = newZip;
    if (newZip.length >= maxLengthUserCanEnter) {
      tempZip = newZip.slice(0, maxLengthUserCanEnter);
      this.zipCodeValidationForm.get('zip').patchValue(tempZip);
      if (this.enteredZip !== tempZip) {
        this.suggestedAddresses = null;
        this.enteredZip = tempZip;
        this.formChange.emit({
          zipCodeData: tempZip,
          formName: 'zipCodeValidationForm',
          formGroup: this.zipCodeValidationForm,
        });
        this.getCityNameForZipCode(tempZip);
        this.logger.debug(
          SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
          SHARED_BROWSE_COMPONENTS.ZIP_CODE,
          ' Entering updateZipCodeInformation() in ' +
            SHARED_BROWSE_COMPONENTS.ZIP_CODE +
            '. Input data: ' +
            tempZip
        );
      }
    }
  }

  /**
   * Function used to get city name for zipcode
   */
  private getCityNameForZipCode(enteredZipCode: string): void {
    this.showSpinner = true;
    this.apiError = false;
    this.sharedBrowseService
      .validateAddress(this.zipCodeValidationForm)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (resp: Address[]) => {
          this.showSpinner = false;
          if (resp && resp.length) {
            this.enteredZip = enteredZipCode;
            this.suggestedAddresses = resp;
            this.formChange.emit({
              zipCodeData: enteredZipCode,
              addressId: this.suggestedAddresses[0].addressid,
              cityName: this.suggestedAddresses[0].cityName,
              stateCode: this.suggestedAddresses[0].stateCode,
              formName: 'zipCodeValidationForm',
              formGroup: this.zipCodeValidationForm,
            });
          }
        },
        (error: APIError) => {
          this.showSpinner = false;
          this.apiError = true;
        },
        () => {
          this.showSpinner = false;
        }
      );
  }

  /**
   * Calls the SharedBrowseService to build zip code validation form.
   * @returns void
   */
  private buildZipCodeValidationForm(): void {
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.ZIP_CODE,
      ' Entering buildZipCodeValidationForm() in ' + SHARED_BROWSE_COMPONENTS.ZIP_CODE
    );
    this.zipCodeValidationForm = this.sharedBrowseService.buildChangeZipForm();
    this.formChange.emit({
      zipCodeData: null,
      formName: 'zipCodeValidationForm',
      formGroup: this.zipCodeValidationForm,
    });
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.ZIP_CODE,
      ' Exiting buildZipCodeValidationForm() in ' + SHARED_BROWSE_COMPONENTS.ZIP_CODE
    );
  }
}
